import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// Auto-refresh interceptor.
// When any request returns 401, try POST /auth/refresh once. If that succeeds,
// retry the original request with the freshly-issued cookies. If refresh also
// fails (refresh token genuinely missing/expired/revoked), let the 401 bubble.
// This is what keeps users "always logged in" until they explicitly sign out.
// ---------------------------------------------------------------------------

let refreshInFlight = null;

const NO_RETRY_PATHS = [
    "/auth/login",
    "/auth/register",
    "/auth/refresh",
    "/auth/me",            // initial bootstrap handles its own refresh
    "/auth/google/session",
    "/auth/forgot-password",
    "/auth/reset-password",
];

api.interceptors.response.use(
    (resp) => resp,
    async (error) => {
        const original = error.config;
        if (!original || error.response?.status !== 401) throw error;
        if (original._retried) throw error;
        if (NO_RETRY_PATHS.some((p) => original.url?.endsWith(p))) throw error;

        original._retried = true;
        try {
            // De-dupe: only one refresh in flight at a time
            if (!refreshInFlight) {
                refreshInFlight = api.post("/auth/refresh").finally(() => { refreshInFlight = null; });
            }
            await refreshInFlight;
            return api(original);
        } catch (refreshErr) {
            throw error;
        }
    }
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
